import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Checkbox, ClickAwayListener, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import useNonInitialEffect from '@versiondos/hooks';
import clsx from 'clsx';
import { useFieldStyles } from 'components/UI/FieldStyles';
import LabelField from 'components/UI/LabelField';
import { FunctionComponent, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { Membership } from '@spike/membership-model';

export interface SelectMembershipProps {
    label?: string | JSX.Element;
    name?: string;
    selectedMembershipId?: number;
    placeholder?: string;
    memberships: Array<Membership>;
    disabled?: boolean;
    required?: boolean;
    id?: string;
    className?: string;
    onSelect: (membershipId: number) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            minWidth: 110
        },
        dropdownContainer: {
            position: 'relative',
            height: 54
        },
        collapsedContainer: {
            height: '100%'
        },
        expandedContainer: {
            display: 'flex',
            flexDirection: 'column',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 10,
            width: '100%',
            backgroundColor: 'white',
            border: '1px solid #222222 !important',
            paddingBottom: 30,
            [theme.breakpoints.up('md')]: {
                paddingBottom: 33
            }
        },
        headerContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            height: 49,
            cursor: 'pointer'
        },
        header: {
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: '#000000',
            fontSize: 16,
            lineHeight: '27px',
            paddingLeft: 17,
            [theme.breakpoints.up('md')]: {
                paddingLeft: 19
            }
        },
        listContainer: {
            borderTop: '1px solid #222222',
            overflowY: 'auto',
            height: 'auto',
            paddingTop: 13,
            maxHeight: 200,
            [theme.breakpoints.up('md')]: {
                paddingTop: 14
            }
        },
        placeholder: {
            color: '#7A7A7A',
            fontSize: 16,
            lineHeight: '27px'
        },
        border: {
            border: '1px solid #222222',
            borderRadius: 27,
            [theme.breakpoints.up('md')]: {
                borderRadius: 30
            }
        },
        textContainer: {
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            height: '100%',
            paddingRight: 10,
            maxWidth: '85%'
        },
        chevronContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            paddingRight: 32,
            [theme.breakpoints.up('md')]: {
                paddingRight: 35
            }
        },
        itemContainer: {
            'display': 'flex',
            'flexDirection': 'column',
            'justifyContent': 'center',
            'height': 33,
            '&:hover': {
                background: '#F8F8F8'
            }
        },
        itemContent: {
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            paddingLeft: 13,
            paddingRight: 14,
            [theme.breakpoints.up('md')]: {
                paddingLeft: 14,
                paddingRight: 16
            }
        },
        name: {
            padding: '3px 5px',
            fontSize: 16,
            lineHeight: '16px',
            [theme.breakpoints.up('md')]: {
                padding: '4px 6px'
            },
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        },
        price: {
            padding: '3px 5px',
            fontSize: 16,
            lineHeight: '16px',
            [theme.breakpoints.up('md')]: {
                padding: '4px 6px'
            }
        },
        optionNotSelected: {
            border: '1px solid #D3D3D3 !important'
        },
        chevron: {
            fontSize: 16
        }
    })
);

export const SelectMembership: FunctionComponent<SelectMembershipProps> = props => {
    const classes = useStyles();
    const fieldClasses = useFieldStyles();

    const [isCollapsed, setIsCollapsed] = useState(true);
    const [selectedId, setSelectedId] = useState(props.selectedMembershipId);

    useNonInitialEffect(() => {
        !isEqual(props.selectedMembershipId, selectedId) && setSelectedId(props.selectedMembershipId);
    }, [props.selectedMembershipId]);

    const clickCollapseHandler = () => {
        setIsCollapsed(prev => !prev);
    };

    const selectIdHandler = (id: number) => {
        setIsCollapsed(true);
        setSelectedId(id);
        props.onSelect(id);
    };

    const header = (
        <Box
            className={classes.headerContainer}
            onClick={clickCollapseHandler}
        >
            <Box className={classes.textContainer}>
                <Typography className={clsx(classes.header, { [classes.placeholder]: selectedId === undefined })}>
                    {props.memberships.find(membership => membership.id === selectedId)?.name ?? props.placeholder}
                </Typography>
            </Box>
            <Box className={classes.chevronContainer}>
                <FontAwesomeIcon
                    icon={isCollapsed ? faChevronDown : faChevronUp}
                    color={isCollapsed ? '#7A7A7A' : '#222222'}
                    className={classes.chevron}
                />
            </Box>
        </Box>
    );

    const list = (
        <Box className={classes.listContainer}>
            {props.memberships.map((membership, index) => (
                <Box
                    className={classes.itemContainer}
                    key={membership.id}
                    id={`selection_pet_${index}`}
                >
                    <Box
                        className={classes.itemContent}
                        onClick={() => selectIdHandler(membership.id ?? 0)}
                    >
                        <Typography className={classes.name}>{membership.name}</Typography>
                        <Typography className={classes.price}>
                            {membership.total ? `$${Number(membership.total).toFixed(2)}` : ''}
                        </Typography>
                    </Box>
                </Box>
            ))}
        </Box>
    );

    const collapsed = (
        <Box className={clsx(classes.border, classes.collapsedContainer, [classes.optionNotSelected])}>{header}</Box>
    );

    const expanded = (
        <Box className={clsx(classes.border, classes.expandedContainer)}>
            {header}
            {list}
        </Box>
    );

    return (
        <ClickAwayListener onClickAway={() => setIsCollapsed(true)}>
            <Box className={clsx(classes.container, fieldClasses.container, props.className)}>
                <LabelField {...props} />
                <Box
                    className={classes.dropdownContainer}
                    id="select_membership"
                >
                    {isCollapsed ? collapsed : expanded}
                </Box>
            </Box>
        </ClickAwayListener>
    );
};

export default SelectMembership;
